import React, { useState, useEffect } from 'react';
import { Book, Download, Lock, Filter, Search, BookOpen, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LibraryPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // all, free, premium
  const [searchQuery, setSearchQuery] = useState('');
  const [libraryItems, setLibraryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLibrary();
  }, []);

  const fetchLibrary = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      console.log('Fetching library from:', `${backendUrl}/api/library/`);
      
      const response = await fetch(`${backendUrl}/api/library/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      console.log('Library response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Library data received:', data.length, 'ebooks');
        
        // Transform backend data to match frontend structure
        const transformedData = data.map(ebook => ({
          id: ebook.id,
          title: ebook.title,
          description: ebook.description,
          category: ebook.category,
          author: ebook.author,
          price: ebook.price || 0,  // Use price from backend (based on membership)
          isFree: ebook.price === 0,  // Free if price is 0
          thumbnail: ebook.cover_url,
          file_url: ebook.file_url,
          file_type: ebook.file_type,
          file_size: ebook.file_size
        }));
        
        console.log('Transformed library items:', transformedData.length);
        setLibraryItems(transformedData);
      } else {
        const errorText = await response.text();
        console.error('Library fetch failed:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching library:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = libraryItems.filter(item => {
    if (filter === 'free') return item.isFree;
    if (filter === 'premium') return !item.isFree;
    return true;
  }).filter(item => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDownload = async (ebook) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/library/${ebook.id}/download-file`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', errorText);
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${ebook.title}.${(ebook.file_type || 'pdf').toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download "${ebook.title}". ${error.message || 'Please try again.'}`);
    }
  };

  // Cover-image trust heuristic: the legacy `cover_url` values point to
  // /api/static/thumbnails/* — these are first-page screenshots auto-scraped
  // from each PDF and routinely show the WRONG book (e.g. "Kingdom Builders"
  // showing the "Honor" cover). Until Pastor uploads curated covers via the
  // admin panel, treat those auto-thumbnails as untrustworthy and render a
  // clean themed cover instead.
  const isTrustedCover = (url) => {
    if (!url) return false;
    if (url.startsWith('/api/static/thumbnails/')) return false;
    if (url.includes('/static/thumbnails/')) return false;
    return true;
  };

  // Deterministic gradient per category — so the same category always renders
  // the same accent colour. Pastor / users can recognise sections at a glance.
  const categoryGradient = (category = '') => {
    const palettes = [
      'from-purple-600 via-purple-700 to-indigo-800',
      'from-amber-500 via-orange-600 to-rose-700',
      'from-emerald-600 via-teal-700 to-cyan-800',
      'from-rose-500 via-pink-600 to-fuchsia-800',
      'from-blue-600 via-indigo-700 to-purple-900',
      'from-yellow-500 via-amber-600 to-orange-800',
      'from-slate-700 via-slate-800 to-zinc-900',
    ];
    let h = 0;
    for (let i = 0; i < category.length; i++) h = (h * 31 + category.charCodeAt(i)) >>> 0;
    return palettes[h % palettes.length];
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
          <div className="flex items-center mb-4 lg:mb-6">
            <Book className="w-8 h-8 lg:w-10 lg:h-10 text-purple-600 mr-3 lg:mr-4" />
            <h1 className="text-2xl lg:text-4xl font-light text-gray-900">Resource Library</h1>
          </div>
          <p className="text-sm lg:text-lg text-gray-600 mb-6 lg:mb-8 max-w-3xl">
            Access a growing collection of ebooks, teaching materials, and resources to deepen your spiritual journey.
          </p>

          {/* Search and Filter */}
          <div className="flex flex-col gap-3 lg:gap-4">
            <div className="relative">
              <Search className="absolute left-3 lg:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 lg:pl-12 pr-4 py-2.5 lg:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm lg:text-base"
              />
            </div>
            <div className="flex space-x-2 overflow-x-auto pb-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 lg:px-6 py-2 lg:py-3 rounded-xl font-medium transition-colors text-sm lg:text-base whitespace-nowrap ${
                  filter === 'all'
                    ? 'bg-black text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('free')}
                className={`px-4 lg:px-6 py-2 lg:py-3 rounded-xl font-medium transition-colors text-sm lg:text-base whitespace-nowrap ${
                  filter === 'free'
                    ? 'bg-black text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Free
              </button>
              <button
                onClick={() => setFilter('premium')}
                className={`px-4 lg:px-6 py-2 lg:py-3 rounded-xl font-medium transition-colors text-sm lg:text-base whitespace-nowrap ${
                  filter === 'premium'
                    ? 'bg-black text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Premium
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Library Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
        {filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No resources found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group cursor-pointer"
              >
                <div className={`relative h-64 bg-gradient-to-br ${categoryGradient(item.category)} overflow-hidden`}>
                  {isTrustedCover(item.thumbnail) ? (
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    // Generated cover: serif title + author + subtle book-spine
                    // styling so each card looks intentional, not broken.
                    <div className="absolute inset-0 flex flex-col justify-between p-6">
                      <div className="text-white/70 text-xs uppercase tracking-widest font-medium">
                        {item.category || 'Resource'}
                      </div>
                      <div>
                        <h4 className="text-white font-serif text-2xl leading-tight drop-shadow-md mb-2 line-clamp-4">
                          {item.title}
                        </h4>
                        {item.author && (
                          <p className="text-white/80 text-sm italic">{item.author}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {item.isFree ? (
                    <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow">
                      FREE
                    </div>
                  ) : (
                    <div className="absolute top-4 right-4 bg-white/90 text-gray-900 px-3 py-1 rounded-full text-xs font-medium shadow">
                      £{item.price.toFixed(2)}
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  
                  {/* Security Badge */}
                  <div className="flex items-center space-x-1 text-xs text-green-600 mb-4">
                    <Shield className="w-3 h-3" />
                    <span>DRM Protected - In-App Reading Only</span>
                  </div>

                  {item.isFree ? (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => navigate(`/library/read/${item.id}`)}
                        data-testid={`read-now-${item.id}`}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 rounded-xl font-medium hover:from-purple-700 hover:to-purple-800 transition-all flex items-center justify-center"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Read Now
                      </button>
                      <button
                        onClick={() => handleDownload(item)}
                        data-testid={`download-${item.id}`}
                        aria-label={`Download ${item.title}`}
                        title="Download PDF"
                        className="px-4 py-3 border-2 border-purple-600 text-purple-700 rounded-xl font-medium hover:bg-purple-50 transition-all flex items-center justify-center"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={async () => {
                        try {
                          const backendUrl = process.env.REACT_APP_BACKEND_URL;
                          const originUrl = window.location.origin;
                          
                          const response = await fetch(`${backendUrl}/api/payments/checkout`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                            },
                            body: JSON.stringify({
                              package_id: 'ebook_purchase',
                              origin_url: originUrl,
                              ebook_id: item.id
                            })
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            window.location.href = data.url;
                          } else if (response.status === 401) {
                            alert('Please log in to purchase ebooks');
                            window.location.href = '/login';
                          } else {
                            const error = await response.json();
                            alert(error.detail || 'Failed to create payment session');
                          }
                        } catch (error) {
                          console.error('Purchase error:', error);
                          alert('Error initiating purchase. Please try again.');
                        }
                      }}
                      className="w-full bg-black text-white py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Purchase for £{item.price.toFixed(2)}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryPage;